import React, { useState } from 'react';
import { X, ShoppingCart, ExternalLink, TrendingUp, AlertCircle, Package, DollarSign, Calendar, Lightbulb } from 'lucide-react';

interface ProductSuggestion {
  itemName: string;
  category: string;
  description: string;
  estimatedPrice: number;
  minPrice: number;
  maxPrice: number;
  quantity: number;
  totalCost: number;
  priority: 'essential' | 'recommended' | 'optional';
  allegro: {
    searchQuery: string;
    directUrl: string;
    alternativeQueries: string[];
  };
  alternativeStores: Array<{
    storeName: string;
    searchUrl: string;
    notes: string;
  }>;
  specifications: string[];
  buyingTips: string[];
  reasonWhy: string;
}

interface PurchaseListReport {
  totalBudget: number;
  suggestedBudget: number;
  categories: Array<{
    name: string;
    totalCost: number;
    items: number;
  }>;
  products: ProductSuggestion[];
  essentialItemsCost: number;
  recommendedItemsCost: number;
  optionalItemsCost: number;
  savingsTips: string[];
  budgetBreakdown: string;
  timeline: string;
  shoppingStrategy: string;
}

interface Props {
  report: PurchaseListReport | null;
  onClose: () => void;
}

export const PurchaseListModal: React.FC<Props> = ({ report, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');

  if (!report) return null;

  const filteredProducts = report.products.filter(product => {
    const categoryMatch = selectedCategory === 'all' || product.category === selectedCategory;
    const priorityMatch = selectedPriority === 'all' || product.priority === selectedPriority;
    return categoryMatch && priorityMatch;
  });

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'essential':
        return <span className="px-3 py-1 text-xs font-bold bg-red-100 text-red-700 rounded-full uppercase">Niezbędne</span>;
      case 'recommended':
        return <span className="px-3 py-1 text-xs font-bold bg-yellow-100 text-yellow-700 rounded-full uppercase">Zalecane</span>;
      case 'optional':
        return <span className="px-3 py-1 text-xs font-bold bg-green-100 text-green-700 rounded-full uppercase">Opcjonalne</span>;
      default:
        return null;
    }
  };

  const getBudgetUsageColor = () => {
    const percentage = (report.suggestedBudget / report.totalBudget) * 100;
    if (percentage <= 90) return 'text-green-600';
    if (percentage <= 100) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">Lista Zakupów z Linkami</h2>
              <p className="text-blue-100 text-sm mt-1">Allegro i sklepy internetowe</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Budget Overview */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 border-b">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
                <DollarSign className="w-4 h-4" />
                <span>Dostępny budżet</span>
              </div>
              <div className="text-2xl font-bold text-gray-800">
                {report.totalBudget.toLocaleString('pl-PL')} zł
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
                <Package className="w-4 h-4" />
                <span>Sugerowany budżet</span>
              </div>
              <div className={`text-2xl font-bold ${getBudgetUsageColor()}`}>
                {report.suggestedBudget.toLocaleString('pl-PL')} zł
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {((report.suggestedBudget / report.totalBudget) * 100).toFixed(1)}% budżetu
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 text-red-600 text-sm mb-1">
                <AlertCircle className="w-4 h-4" />
                <span>Niezbędne</span>
              </div>
              <div className="text-2xl font-bold text-red-700">
                {report.essentialItemsCost.toLocaleString('pl-PL')} zł
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 text-yellow-600 text-sm mb-1">
                <TrendingUp className="w-4 h-4" />
                <span>Zalecane</span>
              </div>
              <div className="text-2xl font-bold text-yellow-700">
                {report.recommendedItemsCost.toLocaleString('pl-PL')} zł
              </div>
            </div>
          </div>

          {/* Budget Breakdown */}
          <div className="mt-4 bg-white rounded-lg p-4 shadow-sm">
            <h3 className="font-semibold text-gray-800 mb-2">Rozkład Budżetu</h3>
            <p className="text-gray-700 text-sm whitespace-pre-line">{report.budgetBreakdown}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 bg-gray-50 border-b flex flex-wrap gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mr-2">Kategoria:</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Wszystkie ({report.products.length})</option>
              {report.categories.map(cat => (
                <option key={cat.name} value={cat.name}>
                  {cat.name} ({cat.items})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mr-2">Priorytet:</label>
            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Wszystkie</option>
              <option value="essential">Niezbędne</option>
              <option value="recommended">Zalecane</option>
              <option value="optional">Opcjonalne</option>
            </select>
          </div>

          <div className="ml-auto text-sm text-gray-600 flex items-center gap-2">
            <Package className="w-4 h-4" />
            <span>Produktów: {filteredProducts.length}</span>
          </div>
        </div>

        {/* Products List */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {filteredProducts.map((product, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition">
                {/* Product Header */}
                <div className="p-4 border-b bg-gray-50">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-gray-900">{product.itemName}</h3>
                        {getPriorityBadge(product.priority)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
                          {product.category}
                        </span>
                        <span>Ilość: {product.quantity} szt.</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">
                        {product.estimatedPrice.toLocaleString('pl-PL')} zł
                      </div>
                      <div className="text-xs text-gray-500">
                        {product.minPrice.toLocaleString('pl-PL')} - {product.maxPrice.toLocaleString('pl-PL')} zł
                      </div>
                      <div className="text-sm font-semibold text-gray-700 mt-1">
                        Razem: {product.totalCost.toLocaleString('pl-PL')} zł
                      </div>
                    </div>
                  </div>
                </div>

                {/* Product Body */}
                <div className="p-4">
                  {/* Description */}
                  <div className="mb-3">
                    <p className="text-gray-700 text-sm">{product.description}</p>
                  </div>

                  {/* Reason Why */}
                  <div className="mb-3 bg-blue-50 border-l-4 border-blue-400 p-3">
                    <div className="flex items-start gap-2">
                      <Lightbulb className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-xs font-semibold text-blue-800 uppercase">Dlaczego potrzebne?</span>
                        <p className="text-sm text-blue-900 mt-1">{product.reasonWhy}</p>
                      </div>
                    </div>
                  </div>

                  {/* Specifications */}
                  {product.specifications.length > 0 && (
                    <div className="mb-3">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">Specyfikacja:</h4>
                      <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                        {product.specifications.map((spec, i) => (
                          <li key={i}>{spec}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Buying Tips */}
                  {product.buyingTips.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">Wskazówki zakupowe:</h4>
                      <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                        {product.buyingTips.map((tip, i) => (
                          <li key={i}>{tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Links Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Allegro Links */}
                    <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                      <h4 className="text-sm font-bold text-orange-800 mb-2 flex items-center gap-2">
                        <ExternalLink className="w-4 h-4" />
                        Allegro
                      </h4>
                      <a
                        href={product.allegro.directUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block mb-2 text-sm text-orange-700 hover:text-orange-900 hover:underline font-medium"
                      >
                        🔍 Szukaj: {product.allegro.searchQuery}
                      </a>
                      {product.allegro.alternativeQueries.length > 0 && (
                        <div className="text-xs text-orange-600 mt-2">
                          <span className="font-semibold">Alternatywne wyszukiwania:</span>
                          <ul className="mt-1 space-y-1">
                            {product.allegro.alternativeQueries.map((query, i) => (
                              <li key={i} className="truncate">• {query}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Alternative Stores */}
                    <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                      <h4 className="text-sm font-bold text-green-800 mb-2 flex items-center gap-2">
                        <ExternalLink className="w-4 h-4" />
                        Alternatywne sklepy
                      </h4>
                      <div className="space-y-2">
                        {product.alternativeStores.map((store, i) => (
                          <div key={i}>
                            <a
                              href={store.searchUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-green-700 hover:text-green-900 hover:underline font-medium"
                            >
                              🏪 {store.storeName}
                            </a>
                            {store.notes && (
                              <p className="text-xs text-green-600 mt-0.5">{store.notes}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Info */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Shopping Strategy */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="font-bold text-purple-800 mb-2 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Strategia Zakupów
              </h3>
              <p className="text-sm text-purple-900 whitespace-pre-line">{report.shoppingStrategy}</p>
            </div>

            {/* Timeline */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Harmonogram
              </h3>
              <p className="text-sm text-blue-900 whitespace-pre-line">{report.timeline}</p>
            </div>
          </div>

          {/* Savings Tips */}
          {report.savingsTips.length > 0 && (
            <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Wskazówki Oszczędnościowe
              </h3>
              <ul className="space-y-2">
                {report.savingsTips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-green-900">
                    <span className="text-green-600 font-bold">💡</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-medium"
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
};
